import { normalizeEnumLabel } from '@doss/common-all'
import { KeyboardEvent, useMemo, useState } from 'react'
import { RxCross2, RxPencil1, RxPlus } from 'react-icons/rx'
import { ShortTextInput } from '~/@/components/inputs/text/short-text-input'
import { Button } from '~/@/components/ui/button'
import { cn } from '~/@/lib/utils'
import { EnumColorPreset } from '~/common/generated/apollo-operations'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { blue, green, orange, purple, red, yellow } from '~/common/styles/colors'
import {
  ENUM_COLOR_PRESETS,
  hexToOklchString,
  oklchStringToHex,
  presetAccentHex,
  resolveEnumOptionColor,
  type ResolvedOptionColor,
} from '~/common/styles/enum-colors'
import { DossIcon } from '~/components/DossIcon'
import { EnumChip } from '~/components/shared/EnumChip'
import { EnumOptionMark } from '~/components/shared/EnumOptionMark'
import { ICON_PICKER_ICON_SIZE, IconPickerGrid } from '~/components/shared/IconPickerGrid'
import { ColorPicker } from './ColorPicker'
import { EnumOptionFormValue } from './types'

interface EnumOptionEditPanelProps {
  option: EnumOptionFormValue
  /** Labels of every other option, so a new label can be checked for duplicates */
  otherLabels: string[]
  onSave: (option: EnumOptionFormValue) => void
  onCancel: () => void
  isMultiSelect: boolean
}

const CELL = 'flex size-7 items-center justify-center rounded'
const SELECTED_CELL = 'outline outline-2 outline-blue-9 outline-offset-2'
// Decorative sweep for the custom-color affordance
const RAINBOW_STOPS = [red[9], orange[9], yellow[10], green[9], blue[9], purple[9], red[9]]
const RAINBOW = `conic-gradient(from 90deg, ${RAINBOW_STOPS.join(', ')})`
const DEFAULT_CUSTOM_HEX = blue[9]
const SWATCH_GLYPH_SIZE = 14

/**
 * Edits one enum option's name, color, and icon. Changes are held locally until saved, so a cancelled
 * edit leaves the option untouched and an unsaved new option never enters the list.
 */
export const EnumOptionEditPanel = ({
  option,
  otherLabels,
  onSave,
  onCancel,
  isMultiSelect,
}: EnumOptionEditPanelProps) => {
  const { t } = useTypedTranslation()
  const [draft, setDraft] = useState(option)
  const [isPickingCustom, setIsPickingCustom] = useState(false)

  const color = resolveEnumOptionColor(draft.colorPreset, draft.colorCustom)
  const customHex = draft.colorCustom ? oklchStringToHex(draft.colorCustom) : null
  const presetColorName = ENUM_COLOR_PRESETS.find(
    ({ preset }) => preset === draft.colorPreset,
  )?.name
  const presetName = presetColorName ? t(`form.field.optionColors.${presetColorName}`) : null

  // Opening the picker from a preset starts from that preset's color
  const pickerSeedHex =
    customHex ?? (draft.colorPreset ? presetAccentHex(draft.colorPreset) : DEFAULT_CUSTOM_HEX)

  // Standing in for the name until one is typed keeps the preview from collapsing to an empty chip
  const previewLabel = draft.label.trim() || t('table.create_column.option_name')

  const takenLabels = useMemo(() => new Set(otherLabels.map(normalizeEnumLabel)), [otherLabels])

  const trimmedLabel = draft.label.trim()
  const duplicateLabelError =
    trimmedLabel !== '' && takenLabels.has(normalizeEnumLabel(trimmedLabel))
      ? t('table.create_column.duplicate_option_name')
      : undefined
  const canSave = trimmedLabel !== '' && duplicateLabelError === undefined

  const selectPreset = (preset: EnumColorPreset) => {
    setDraft({ ...draft, colorPreset: preset, colorCustom: null })
    setIsPickingCustom(false)
  }

  const selectCustom = (hex: string) => {
    setDraft({ ...draft, colorPreset: null, colorCustom: hexToOklchString(hex) })
  }

  const toggleCustomPicker = () => {
    const isOpening = !isPickingCustom
    setIsPickingCustom(isOpening)
    // Opening the picker with no color yet commits the color it starts on, so it can be kept as-is.
    // Opening it from a preset leaves the preset alone until the picker is actually moved.
    if (isOpening && !draft.colorCustom && !draft.colorPreset) {
      setDraft({ ...draft, colorCustom: hexToOklchString(pickerSeedHex) })
    }
  }

  const clearColor = () => {
    setDraft({ ...draft, colorPreset: null, colorCustom: null })
    setIsPickingCustom(false)
  }

  const handleSave = () => {
    if (!canSave) {
      return
    }
    onSave({ ...draft, label: trimmedLabel })
  }

  /**
   * A preset cell echoes the shape a cell uses — a tinted fill for multi-select, a dot or colored icon
   * for single-select. Sizes are chosen to read evenly in the grid, not to match a cell's.
   */
  const renderPresetPreview = (presetColor: ResolvedOptionColor | null) => {
    if (isMultiSelect) {
      return (
        <span
          className="flex size-full items-center justify-center rounded border"
          style={{ backgroundColor: presetColor?.fill, borderColor: presetColor?.border }}
        >
          {draft.iconName && (
            <DossIcon color={presetColor?.content} name={draft.iconName} size={SWATCH_GLYPH_SIZE} />
          )}
        </span>
      )
    }

    if (draft.iconName) {
      return (
        <DossIcon color={presetColor?.vivid} name={draft.iconName} size={ICON_PICKER_ICON_SIZE} />
      )
    }

    return (
      <span className="size-2.5 rounded-full" style={{ backgroundColor: presetColor?.vivid }} />
    )
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    // The modifier is required, matching the column form's own save shortcut, so that pressing enter
    // while naming an option can't commit it by accident
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      handleSave()
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      onCancel()
    }
  }

  return (
    <div
      className="w-[436px] rounded-lg border border-gray-6 bg-gray-1 shadow-lg"
      onKeyDown={handleKeyDown}
    >
      <div className="flex flex-col gap-2 px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          {isMultiSelect ? (
            <EnumChip color={color} iconName={draft.iconName} label={previewLabel} />
          ) : (
            <>
              <EnumOptionMark color={color} iconName={draft.iconName} />
              <span className="truncate text-sm font-medium">{previewLabel}</span>
            </>
          )}

          <Button
            className="ms-auto size-6 shrink-0 p-0"
            onClick={onCancel}
            title={t('common.actions.close')}
            type="button"
            variant="ghost"
          >
            <RxCross2 size={14} />
          </Button>
        </div>

        {!option.isSaved && (
          <ShortTextInput
            autoFocus
            errorText={duplicateLabelError}
            onChange={(event) => setDraft({ ...draft, label: event.target.value })}
            value={draft.label}
            // InputWrapper pads itself, which would inset the field from the preview above it
            wrapperClassName="p-0"
          />
        )}
      </div>

      {/* A fixed body height keeps the popover from resizing as a color, an icon, or the custom
          picker is opened; the icon grid takes up whatever height the color column leaves */}
      <div className="flex h-[360px] border-t border-gray-4">
        <div className="w-[192px] shrink-0 overflow-y-auto border-e border-gray-4 px-4 py-3">
          <div className="mb-2 flex h-7 items-center justify-between">
            <span className="text-sm text-gray-11">{t('table.create_column.option_color')}</span>
            {color && (
              <Button className="px-0" onClick={clearColor} size="xs" type="button" variant="ghost">
                {t('common.actions.clear')}
              </Button>
            )}
          </div>

          <div className="grid grid-cols-4 gap-4">
            {ENUM_COLOR_PRESETS.map(({ preset, name }) => {
              const presetColor = resolveEnumOptionColor(preset)
              return (
                <button
                  key={preset}
                  className={cn(
                    CELL,
                    // While the custom picker is open it owns the selection, so a preset chosen
                    // earlier stops reading as selected
                    !isPickingCustom && draft.colorPreset === preset && SELECTED_CELL,
                  )}
                  onClick={() => selectPreset(preset)}
                  title={t(`form.field.optionColors.${name}`)}
                  type="button"
                >
                  {renderPresetPreview(presetColor)}
                </button>
              )
            })}

            <button
              className={cn(CELL, (draft.colorCustom || isPickingCustom) && SELECTED_CELL)}
              onClick={toggleCustomPicker}
              type="button"
            >
              {draft.colorCustom && color ? (
                <span
                  className="flex size-full items-center justify-center rounded border"
                  style={{ backgroundColor: color.fill, borderColor: color.border }}
                >
                  {!isPickingCustom && <RxPencil1 color={color.content} size={SWATCH_GLYPH_SIZE} />}
                </span>
              ) : (
                <span
                  className="flex size-full items-center justify-center rounded"
                  style={{ background: RAINBOW }}
                >
                  <RxPlus className="text-white" size={SWATCH_GLYPH_SIZE} />
                </span>
              )}
            </button>
          </div>

          {isPickingCustom ? (
            <ColorPicker className="mt-4" color={pickerSeedHex} onChange={selectCustom} />
          ) : (
            <div className="mt-4 h-5 text-xs text-gray-11">{customHex ?? presetName}</div>
          )}
        </div>

        <div className="flex min-h-0 w-[244px] shrink-0 flex-col px-4 py-3">
          <div className="mb-2 flex h-7 items-center justify-between">
            <span className="text-sm text-gray-11">{t('table.create_column.option_icon')}</span>
            {draft.iconId && (
              <Button
                className="px-0"
                onClick={() => setDraft({ ...draft, iconId: null, iconName: null })}
                size="xs"
                type="button"
                variant="ghost"
              >
                {t('common.actions.clear')}
              </Button>
            )}
          </div>

          <IconPickerGrid
            className="min-h-0 flex-1"
            columns={6}
            gridClassName="min-h-0 flex-1 content-start"
            onSelect={(icon) =>
              setDraft(
                draft.iconId === icon.id
                  ? { ...draft, iconId: null, iconName: null }
                  : { ...draft, iconId: icon.id, iconName: icon.name },
              )
            }
            selectedIconName={draft.iconName}
          />
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 border-t border-gray-4 px-4 py-3">
        <Button onClick={onCancel} size="sm" type="button" variant="ghost">
          {t('common.actions.cancel')}
        </Button>
        <Button disabled={!canSave} onClick={handleSave} size="sm" type="button" variant="primary">
          {option.isSaved ? t('common.actions.done') : t('table.create_column.add_option')} ⌘↵
        </Button>
      </div>
    </div>
  )
}
